// mostly copied from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/VRMLLoader.js
import ExternalLoader, {
  GeometryRawData,
  LoaderDataType,
  Material,
  ObjectData
} from "_src/engine/loaders/external/external_loader";
import output from "_src/engine/output";
import {CstParser, createToken, Lexer} from "chevrotain";

export default class VrmlLoader extends ExternalLoader {
  constructor() {
    super();
    this._dataType = LoaderDataType.TEXT;
  }

  _parse(data, progress = null) {
    const nodeMap = {};

    function generateVRMLTree( data ) {

      // create lexer, parser and visitor

      const tokenData = createTokens();

      const lexer = new VRMLLexer( tokenData.tokens );
      const parser = new VRMLParser( tokenData.tokenVocabulary );
      const visitor = createVisitor( parser.getBaseCstVisitorConstructor() );

      // lexing

      const lexingResult = lexer.lex( data );
      parser.input = lexingResult.tokens;

      // parsing

      const cstOutput = parser.vrml();

      if ( parser.errors.length > 0 ) {

        throw Error( "ExternalLoader.VRMLLoader: Parsing errors detected" );

      }

      // actions

      const ast = visitor.visit( cstOutput );

      return ast;

    }

    function createTokens() {

      // from http://gun.teipir.gr/VRML-amgem/spec/part1/concepts.html#SyntaxBasics

      const RouteIdentifier = createToken( { name: "RouteIdentifier", pattern: /[^\x30-\x39\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d][^\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d]*[.][^\x30-\x39\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d][^\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d]*/ } );
      const Identifier = createToken( { name: "Identifier", pattern: /[^\x30-\x39\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d][^\0-\x20\x22\x27\x23\x2b\x2c\x2d\x2e\x5b\x5d\x5c\x7b\x7d]*/, longer_alt: RouteIdentifier } );

      // from http://gun.teipir.gr/VRML-amgem/spec/part1/nodesRef.html

      const nodeTypes = [
        "Anchor", "Billboard", "Collision", "Group", "Transform", // grouping nodes
        "Inline", "LOD", "Switch", // special groups
        "AudioClip", "DirectionalLight", "PointLight", "Script", "Shape", "Sound", "SpotLight", "WorldInfo", // common nodes
        "CylinderSensor", "PlaneSensor", "ProximitySensor", "SphereSensor", "TimeSensor", "TouchSensor", "VisibilitySensor", // sensors
        "Box", "Cone", "Cylinder", "ElevationGrid", "Extrusion", "IndexedFaceSet", "IndexedLineSet", "PointSet", "Sphere", // geometries
        "Color", "Coordinate", "Normal", "TextureCoordinate", // geometric properties
        "Appearance", "FontStyle", "ImageTexture", "Material", "MovieTexture", "PixelTexture", "TextureTransform", // appearance
        "ColorInterpolator", "CoordinateInterpolator", "NormalInterpolator", "OrientationInterpolator", "PositionInterpolator", "ScalarInterpolator", // interpolators
        "Background", "Fog", "NavigationInfo", "Viewpoint", // bindable nodes
        "Text" // Text must be placed at the end of the regex so there are no matches for TextureTransform and TextureCoordinate
      ];

      //

      const Version = createToken( {
        name: "Version",
        pattern: /#VRML.*/,
        longer_alt: Identifier
      } );

      const NodeName = createToken( {
        name: "NodeName",
        pattern: new RegExp( nodeTypes.join( "|" ) ),
        longer_alt: Identifier
      } );

      const DEF = createToken( {
        name: "DEF",
        pattern: /DEF/,
        longer_alt: Identifier
      } );

      const USE = createToken( {
        name: "USE",
        pattern: /USE/,
        longer_alt: Identifier
      } );

      const ROUTE = createToken( {
        name: "ROUTE",
        pattern: /ROUTE/,
        longer_alt: Identifier
      } );

      const TO = createToken( {
        name: "TO",
        pattern: /TO/,
        longer_alt: Identifier
      } );

      //

      const StringLiteral = createToken( { name: "StringLiteral", pattern: /"(?:[^\\"\n\r]|\\[bfnrtv"\\/]|\\u[0-9a-fA-F][0-9a-fA-F][0-9a-fA-F][0-9a-fA-F])*"/ } );
      const HexLiteral = createToken( { name: "HexLiteral", pattern: /0[xX][0-9a-fA-F]+/ } );
      const NumberLiteral = createToken( { name: "NumberLiteral", pattern: /[-+]?[0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?/ } );
      const TrueLiteral = createToken( { name: "TrueLiteral", pattern: /TRUE/ } );
      const FalseLiteral = createToken( { name: "FalseLiteral", pattern: /FALSE/ } );
      const NullLiteral = createToken( { name: "NullLiteral", pattern: /NULL/ } );
      const LSquare = createToken( { name: "LSquare", pattern: /\[/ } );
      const RSquare = createToken( { name: "RSquare", pattern: /]/ } );
      const LCurly = createToken( { name: "LCurly", pattern: /{/ } );
      const RCurly = createToken( { name: "RCurly", pattern: /}/ } );
      const Comment = createToken( {
        name: "Comment",
        pattern: /#.*/,
        group: Lexer.SKIPPED
      } );

      // commas, blanks, tabs, newlines and carriage returns are whitespace characters wherever they appear outside of string fields

      const WhiteSpace = createToken( {
        name: "WhiteSpace",
        pattern: /[ ,\s]/,
        group: Lexer.SKIPPED
      } );

      const tokens = [
        WhiteSpace,
        // keywords appear before the Identifier
        NodeName,
        DEF,
        USE,
        ROUTE,
        TO,
        TrueLiteral,
        FalseLiteral,
        NullLiteral,
        // the Identifier must appear after the keywords because all keywords are valid identifiers
        Version,
        Identifier,
        RouteIdentifier,
        StringLiteral,
        HexLiteral,
        NumberLiteral,
        LSquare,
        RSquare,
        LCurly,
        RCurly,
        Comment
      ];

      const tokenVocabulary = {};

      for ( let i = 0, l = tokens.length; i < l; i ++ ) {

        const token = tokens[ i ];

        tokenVocabulary[ token.name ] = token;

      }

      return { tokens: tokens, tokenVocabulary: tokenVocabulary };

    }


    function createVisitor( BaseVRMLVisitor ) {

      // the visitor is created dynamically based on the given base class

      class VRMLToASTVisitor extends BaseVRMLVisitor {

        constructor() {

          super();

          this.validateVisitor();

        }

        vrml( ctx ) {

          const data = {
            version: this.visit( ctx.version ),
            nodes: [],
            routes: []
          };

          for ( let i = 0, l = ctx.node.length; i < l; i ++ ) {

            const node = ctx.node[ i ];

            data.nodes.push( this.visit( node ) );

          }

          if ( ctx.route ) {

            for ( let i = 0, l = ctx.route.length; i < l; i ++ ) {

              const route = ctx.route[ i ];

              data.routes.push( this.visit( route ) );

            }

          }

          return data;

        }

        version( ctx ) {

          return ctx.Version[ 0 ].image;

        }

        node( ctx ) {

          const data = {
            name: ctx.NodeName[ 0 ].image,
            fields: []
          };

          if ( ctx.field ) {

            for ( let i = 0, l = ctx.field.length; i < l; i ++ ) {

              const field = ctx.field[ i ];

              data.fields.push( this.visit( field ) );

            }

          }

          // DEF

          if ( ctx.def ) {

            data.DEF = this.visit( ctx.def[ 0 ] );

          }

          return data;

        }

        field( ctx ) {

          const data = {
            name: ctx.Identifier[ 0 ].image,
            type: null,
            values: null
          };

          let result;

          // SFValue

          if ( ctx.singleFieldValue ) {

            result = this.visit( ctx.singleFieldValue[ 0 ] );

          }

          // MFValue

          if ( ctx.multiFieldValue ) {

            result = this.visit( ctx.multiFieldValue[ 0 ] );

          }

          data.type = result.type;
          data.values = result.values;

          return data;

        }

        def( ctx ) {

          return ( ctx.Identifier || ctx.NodeName )[ 0 ].image;

        }

        use( ctx ) {

          return { USE: ( ctx.Identifier || ctx.NodeName )[ 0 ].image };

        }

        singleFieldValue( ctx ) {

          return processField( this, ctx );

        }

        multiFieldValue( ctx ) {

          return processField( this, ctx );

        }

        route( ctx ) {

          const data = {
            FROM: ctx.RouteIdentifier[ 0 ].image,
            TO: ctx.RouteIdentifier[ 1 ].image
          };

          return data;

        }

      }

      function processField( scope, ctx ) {

        const field = {
          type: null,
          values: []
        };

        if ( ctx.node ) {

          field.type = "node";

          for ( let i = 0, l = ctx.node.length; i < l; i ++ ) {

            const node = ctx.node[ i ];

            field.values.push( scope.visit( node ) );

          }

        }

        if ( ctx.use ) {

          field.type = "use";

          for ( let i = 0, l = ctx.use.length; i < l; i ++ ) {

            const use = ctx.use[ i ];

            field.values.push( scope.visit( use ) );

          }

        }

        if ( ctx.StringLiteral ) {

          field.type = "string";

          for ( let i = 0, l = ctx.StringLiteral.length; i < l; i ++ ) {

            const stringLiteral = ctx.StringLiteral[ i ];

            field.values.push( stringLiteral.image.replace( /'|"/g, "" ) );

          }

        }

        if ( ctx.NumberLiteral ) {

          field.type = "number";

          for ( let i = 0, l = ctx.NumberLiteral.length; i < l; i ++ ) {

            const numberLiteral = ctx.NumberLiteral[ i ];

            field.values.push( parseFloat( numberLiteral.image ) );

          }

        }

        if ( ctx.HexLiteral ) {

          field.type = "hex";

          for ( let i = 0, l = ctx.HexLiteral.length; i < l; i ++ ) {

            const hexLiteral = ctx.HexLiteral[ i ];

            field.values.push( hexLiteral.image );

          }

        }

        if ( ctx.TrueLiteral ) {

          field.type = "boolean";

          for ( let i = 0, l = ctx.TrueLiteral.length; i < l; i ++ ) {

            const trueLiteral = ctx.TrueLiteral[ i ];

            if ( trueLiteral.image === "TRUE" ) field.values.push( true );

          }

        }

        if ( ctx.FalseLiteral ) {

          field.type = "boolean";

          for ( let i = 0, l = ctx.FalseLiteral.length; i < l; i ++ ) {

            const falseLiteral = ctx.FalseLiteral[ i ];

            if ( falseLiteral.image === "FALSE" ) field.values.push( false );

          }

        }

        if ( ctx.NullLiteral ) {

          field.type = "null";

          ctx.NullLiteral.forEach( function () {

            field.values.push( null );

          } );

        }

        return field;

      }

      return new VRMLToASTVisitor();

    }

    function parseTree( tree ) {

      const nodes = tree.nodes;

      for ( let i = 0, l = nodes.length; i < l; i ++ ) {

        const node = nodes[ i ];

        buildNodeMap( node );

      }

      for ( let i = 0, l = nodes.length; i < l; i ++ ) {
        const node = nodes[ i ];
        getNode( node );
      }

      const objectsData = [];
      for (const node of nodes) {
        parseObject(node.build, objectsData);
      }

      return objectsData;
    }

    const parseObject = (object, objectsData) => {
      if (object instanceof Group) {
        for (const elem of object.elements) {
          parseObject(elem, objectsData);
        }
      } else if (object instanceof Mesh) {
        if (object.geometry.positions && object.geometry.positions.length) {
          const geometryData = new GeometryRawData(object.geometry.positions);
          geometryData.indices = object.geometry.indices;
          geometryData.normals = object.geometry.normals;
          geometryData.uvs = object.geometry.uvs;

          const materialData = new Material();
          if (object.texture) materialData.baseTexture = this._basePath + object.texture.url;

          const objectData = new ObjectData(geometryData, materialData);
          objectsData.push(objectData);
        }
      }
    }

    function buildNodeMap( node ) {

      if ( node.DEF ) {

        nodeMap[ node.DEF ] = node;

      }

      const fields = node.fields;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];

        if ( field.type === "node" ) {

          const fieldValues = field.values;

          for ( let j = 0, jl = fieldValues.length; j < jl; j ++ ) {

            buildNodeMap( fieldValues[ j ] );

          }

        }


      }

    }


    function getNode( node ) {

      // handle case where a node refers to a different one

      if ( node.USE ) {

        return resolveUSE( node.USE );

      }

      if ( node.build !== undefined ) return node.build;

      node.build = buildNode( node );

      return node.build;

    }

    // node builder

    function buildNode( node ) {

      const nodeName = node.name;
      let build;

      switch ( nodeName ) {

        case "Anchor":
        case "Group":
        case "Transform":
        case "Collision":
          build = buildGroupingNode( node );
          break;

        case "Shape":
          build = buildShapeNode( node );
          break;

        case "Appearance":
          build = buildAppearanceNode( node );
          break;

        case "ImageTexture":
          build = buildImageTextureNode( node );
          break;

        case "IndexedFaceSet":
          build = buildIndexedFaceSetNode( node );
          break;

        case "Color":
        case "Coordinate":
        case "Normal":
        case "TextureCoordinate":
          build = buildGeometricNode( node );
          break;

        case "Material":
        case "PixelTexture":
        case "TextureTransform":
        case "IndexedLineSet":
        case "PointSet":
        case "Box":
        case "Cone":
        case "Cylinder":
        case "Sphere":
        case "ElevationGrid":
        case "Extrusion":
        case "WorldInfo":
        case "Background":
        case "Billboard":
        case "Inline":
        case "LOD":
        case "Switch":
        case "AudioClip":
        case "DirectionalLight":
        case "PointLight":
        case "Script":
        case "Sound":
        case "SpotLight":
        case "CylinderSensor":
        case "PlaneSensor":
        case "ProximitySensor":
        case "SphereSensor":
        case "TimeSensor":
        case "TouchSensor":
        case "VisibilitySensor":
        case "Text":
        case "FontStyle":
        case "MovieTexture":
        case "ColorInterpolator":
        case "CoordinateInterpolator":
        case "NormalInterpolator":
        case "OrientationInterpolator":
        case "PositionInterpolator":
        case "ScalarInterpolator":
        case "Fog":
        case "NavigationInfo":
        case "Viewpoint":
          // node is not supported yet
          break;

        default:
          output.warn( "ExternalLoader.VRMLLoader: Unknown node: " + nodeName );
          break;

      }

      if ( build !== undefined && node.DEF !== undefined && build.hasOwnProperty( "name" ) === true ) {

        build.name = node.DEF;

      }

      return build;

    }

    function buildGroupingNode( node ) {

      const group = new Group();

      //

      const fields = node.fields;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];
        const fieldName = field.name;
        const fieldValues = field.values;

        switch ( fieldName ) {
          case "children":
            parseFieldChildren( fieldValues, group );
            break;

          case "bboxCenter":
          case "bboxSize":
          case "center":
          case "description":
          case "collide":
          case "parameter":
          case "rotation":
          case "scale":
          case "scaleOrientation":
          case "translation":
          case "proxy":
          case "url":
            // field not supported
            break;

          default:
            output.warn( "ExternalLoader.VRMLLoader: Unknown field: " + fieldName );
            break;

        }

      }

      return group;
    }

    function buildShapeNode( node ) {

      const fields = node.fields;

      // if the appearance field is NULL or unspecified, lighting is off and the unlit object color is (0, 0, 0)

      let texture;
      let geometry;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];
        const fieldName = field.name;
        const fieldValues = field.values;

        switch ( fieldName ) {

          case "appearance":
            if ( fieldValues[ 0 ] !== null ) {

              texture = getNode( fieldValues[ 0 ] );

            }

            break;

          case "geometry":
            if ( fieldValues[ 0 ] !== null ) {

              geometry = getNode( fieldValues[ 0 ] );

            }

            break;

          default:
            output.warn( "ExternalLoader.VRMLLoader: Unknown field:" + fieldName );
            break;

        }

      }

      // build 3D object

      let object = null;
      if ( geometry && geometry.positions ) {
        object = new Mesh();
        object.geometry = geometry;
        if ( texture && texture.url ) {
          object.texture = texture;
        }
      }

      return object;

    }

    function buildAppearanceNode( node ) {

      let material;
      const fields = node.fields;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];
        const fieldName = field.name;
        const fieldValues = field.values;

        switch ( fieldName ) {
          case "texture": {
            const textureNode = fieldValues[0];
            if (textureNode !== null) {

              if (textureNode.name === "ImageTexture") {

                material = getNode(textureNode);
              } else {
                // Other types are not supported yet
              }
            }
            break;
          }
          case "material":
          case "textureTransform":
            //not supported
            break;

          default:
            output.warn( "ExternalLoader.VRMLLoader: Unknown field: " + fieldName );
            break;

        }

      }

      return material;

    }

    function buildImageTextureNode( node ) {

      let texture;

      const fields = node.fields;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];
        const fieldName = field.name;
        const fieldValues = field.values;

        switch ( fieldName ) {

          case "url": {
            const url = fieldValues[0];
            if (url) {
              texture = new Texture();
              texture.url = url;
            }
            break;
          }

          case "repeatS":
          case "repeatT":
            //not supported
            break;

          default:
            output.warn( "ExternalLoader.VRMLLoader: Unknown field: " + fieldName );
            break;

        }

      }

      return texture;

    }

    function buildGeometricNode( node ) {

      return node.fields[ 0 ].values;

    }

    function buildIndexedFaceSetNode( node ) {

      let color, coord, normal, texCoord;
      let ccw = true;
      let colorIndex, coordIndex, normalIndex, texCoordIndex;
      let colorPerVertex = true, normalPerVertex = true;

      const fields = node.fields;

      for ( let i = 0, l = fields.length; i < l; i ++ ) {

        const field = fields[ i ];
        const fieldName = field.name;
        const fieldValues = field.values;

        switch ( fieldName ) {

          case "color": {
            const colorNode = fieldValues[0];

            if (colorNode !== null) {

              color = getNode(colorNode);

            }

            break;
          }
          case "coord": {
            const coordNode = fieldValues[0];

            if (coordNode !== null) {

              coord = getNode(coordNode);

            }

            break;
          }
          case "normal": {
            const normalNode = fieldValues[0];

            if (normalNode !== null) {

              normal = getNode(normalNode);

            }

            break;
          }
          case "texCoord": {
            const texCoordNode = fieldValues[0];

            if (texCoordNode !== null) {

              texCoord = getNode(texCoordNode);

            }

            break;
          }
          case "ccw":
            ccw = fieldValues[ 0 ];
            break;

          case "colorIndex":
            colorIndex = fieldValues;
            break;

          case "colorPerVertex":
            colorPerVertex = fieldValues[ 0 ];
            break;

          case "coordIndex":
            coordIndex = fieldValues;
            break;

          case "normalIndex":
            normalIndex = fieldValues;
            break;

          case "normalPerVertex":
            normalPerVertex = fieldValues[ 0 ];
            break;

          case "texCoordIndex":
            texCoordIndex = fieldValues;
            break;

          case "convex":
          case "creaseAngle":
          case "solid":
            // field is not supported
            break;


          default:
            output.warn( "ExternalLoader.VRMLLoader: Unknown field: " + fieldName );
            break;

        }

      }

      if ( coordIndex === undefined ) {

        output.warn( "ExternalLoader.VRMLLoader: Missing coordIndex" );

        return new Geometry(); // handle VRML files with incomplete geometry definition

      }

      const triangulatedCoordIndex = triangulateFaceIndex( coordIndex, ccw );

      let colorAttribute;
      let normalAttribute;
      let uvAttribute;

      if ( color ) {

        if ( colorPerVertex === true ) {

          if ( colorIndex && colorIndex.length > 0 ) {

            // if the colorIndex field is not empty, then it is used to choose colors for each vertex of the IndexedFaceSet.

            const triangulatedColorIndex = triangulateFaceIndex( colorIndex, ccw );
            colorAttribute = computeAttributeFromIndexedData( triangulatedCoordIndex, triangulatedColorIndex, color, 3 );

          } else {

            // if the colorIndex field is empty, then the coordIndex field is used to choose colors from the Color node

            colorAttribute = toNonIndexedAttribute( triangulatedCoordIndex, color, 3 );

          }

        } else {

          if ( colorIndex && colorIndex.length > 0 ) {

            // if the colorIndex field is not empty, then they are used to choose one color for each face of the IndexedFaceSet

            const flattenFaceColors = flattenData( color, colorIndex );
            const triangulatedFaceColors = triangulateFaceData( flattenFaceColors, coordIndex );
            colorAttribute = computeAttributeFromFaceData( triangulatedCoordIndex, triangulatedFaceColors );

          } else {

            // if the colorIndex field is empty, then the color are applied to each face of the IndexedFaceSet in order

            const triangulatedFaceColors = triangulateFaceData( color, coordIndex );
            colorAttribute = computeAttributeFromFaceData( triangulatedCoordIndex, triangulatedFaceColors );


          }

        }

      }

      if ( normal ) {

        if ( normalPerVertex === true ) {

          // consider vertex normals

          if ( normalIndex && normalIndex.length > 0 ) {

            // if the normalIndex field is not empty, then it is used to choose normals for each vertex of the IndexedFaceSet.

            const triangulatedNormalIndex = triangulateFaceIndex( normalIndex, ccw );
            normalAttribute = computeAttributeFromIndexedData( triangulatedCoordIndex, triangulatedNormalIndex, normal, 3 );

          } else {

            // if the normalIndex field is empty, then the coordIndex field is used to choose normals from the Normal node

            normalAttribute = toNonIndexedAttribute( triangulatedCoordIndex, normal, 3 );

          }

        } else {

          // consider face normals

          if ( normalIndex && normalIndex.length > 0 ) {

            // if the normalIndex field is not empty, then they are used to choose one normal for each face of the IndexedFaceSet

            const flattenFaceNormals = flattenData( normal, normalIndex );
            const triangulatedFaceNormals = triangulateFaceData( flattenFaceNormals, coordIndex );
            normalAttribute = computeAttributeFromFaceData( triangulatedCoordIndex, triangulatedFaceNormals );

          } else {

            // if the normalIndex field is empty, then the normals are applied to each face of the IndexedFaceSet in order

            const triangulatedFaceNormals = triangulateFaceData( normal, coordIndex );
            normalAttribute = computeAttributeFromFaceData( triangulatedCoordIndex, triangulatedFaceNormals );

          }

        }

      }

      if ( texCoord ) {

        // texture coordinates are always defined on vertex level

        if ( texCoordIndex && texCoordIndex.length > 0 ) {

          // if the texCoordIndex field is not empty, then it is used to choose texture coordinates for each vertex of the IndexedFaceSet.

          const triangulatedTexCoordIndex = triangulateFaceIndex( texCoordIndex, ccw );
          uvAttribute = computeAttributeFromIndexedData( triangulatedCoordIndex, triangulatedTexCoordIndex, texCoord, 2 );


        } else {

          // if the texCoordIndex field is empty, then the coordIndex array is used to choose texture coordinates from the TextureCoordinate node

          uvAttribute = toNonIndexedAttribute( triangulatedCoordIndex, texCoord, 2);

        }

      }

      const geometry = new Geometry();
      const positionAttribute = toNonIndexedAttribute( triangulatedCoordIndex, coord, 3 );
      geometry.positions = positionAttribute;
      if (normalAttribute) geometry.normals = normalAttribute;
      if ( uvAttribute ) geometry.uvs = uvAttribute;
      if ( colorAttribute ) geometry.colors = colorAttribute;

      return geometry;

    }

    // helper functions

    function resolveUSE( identifier ) {

      const node = nodeMap[ identifier ];
      const build = getNode( node );

      return build;

    }

    function parseFieldChildren( children, group ) {

      for ( let i = 0, l = children.length; i < l; i ++ ) {

        const object = getNode( children[ i ] );

        if ( object instanceof Object3d ) group.elements.push( object );

      }

    }

    function triangulateFaceIndex( index, ccw ) {

      const indices = [];

      // since face defintions can have more than three vertices, it's necessary to
      // perform a simple triangulation

      let start = 0;

      for ( let i = 0, l = index.length; i < l; i ++ ) {

        const i1 = index[ start ];
        const i2 = index[ i + ( ccw ? 1 : 2 ) ];
        const i3 = index[ i + ( ccw ? 2 : 1 ) ];

        indices.push( i1, i2, i3 );

        // an index of -1 indicates that the current face has ended and the next one begins

        if ( index[ i + 3 ] === - 1 || i + 3 >= l ) {

          i += 3;
          start = i + 1;

        }

      }

      return indices;

    }

    function triangulateFaceData( data, index ) {

      const triangulatedData = [];

      let start = 0;

      for ( let i = 0, l = index.length; i < l; i ++ ) {

        const stride = start * 3;

        const x = data[ stride ];
        const y = data[ stride + 1 ];
        const z = data[ stride + 2 ];

        triangulatedData.push( x, y, z );

        // an index of -1 indicates that the current face has ended and the next one begins

        if ( index[ i + 3 ] === - 1 || i + 3 >= l ) {

          i += 3;
          start ++;

        }

      }

      return triangulatedData;

    }

    function flattenData( data, index ) {

      const flattenData = [];

      for ( let i = 0, l = index.length; i < l; i ++ ) {

        const i1 = index[ i ];

        const stride = i1 * 3;

        const x = data[ stride ];
        const y = data[ stride + 1 ];
        const z = data[ stride + 2 ];

        flattenData.push( x, y, z );

      }

      return flattenData;

    }

    function computeAttributeFromIndexedData( coordIndex, index, data, itemSize ) {

      const array = [];

      // we use the coordIndex.length as delimiter since normalIndex must contain at least as many indices

      for ( let i = 0, l = coordIndex.length; i < l; i += 3 ) {

        const a = index[ i ];
        const b = index[ i + 1 ];
        const c = index[ i + 2 ];

        if ( itemSize === 2 ) {

          array.push( data[a*itemSize], data[a*itemSize + 1] );
          array.push( data[b*itemSize], data[b*itemSize + 1] );
          array.push( data[c*itemSize], data[c*itemSize + 1] );

        } else {

          array.push( data[a*itemSize], data[a*itemSize + 1], data[a*itemSize + 2] );
          array.push( data[b*itemSize], data[b*itemSize + 1], data[b*itemSize + 2] );
          array.push( data[c*itemSize], data[c*itemSize + 1], data[c*itemSize + 2] );

        }

      }

      return array;

    }

    function computeAttributeFromFaceData( index, faceData ) {

      const array = [];

      for ( let i = 0, j = 0, l = index.length; i < l; i += 3, j ++ ) {

        array.push( faceData[j*3], faceData[j*3 + 1], faceData[j*3 + 2] );
        array.push( faceData[j*3], faceData[j*3 + 1], faceData[j*3 + 2] );
        array.push( faceData[j*3], faceData[j*3 + 1], faceData[j*3 + 2] );

      }

      return array;

    }

    function toNonIndexedAttribute( indices, array, itemSize ) {

      const array2 = new array.constructor( indices.length * itemSize );

      let index = 0, index2 = 0;

      for ( let i = 0, l = indices.length; i < l; i ++ ) {

        index = indices[ i ] * itemSize;

        for ( let j = 0; j < itemSize; j ++ ) {

          array2[ index2 ++ ] = array[ index ++ ];

        }

      }

      return array2;

    }

    //

    // check version (only 2.0 is supported)

    if ( data.indexOf( "#VRML V2.0" ) === - 1 ) {

      throw Error( "ExternalLoader.VRMLLoader: Version of VRML asset not supported" );

    }

    // create JSON representing the tree structure of the VRML asset

    const tree = generateVRMLTree( data );

    // parse the tree structure to a three.js scene

    const objectsData = parseTree( tree );

    if (progress) progress.emit(progress.LOADING, 1);

    return objectsData;

  }
}

class VRMLLexer {

  constructor( tokens ) {

    this.lexer = new Lexer( tokens );

  }

  lex( inputText ) {

    const lexingResult = this.lexer.tokenize( inputText );

    if ( lexingResult.errors.length > 0 ) {

      throw Error( "ExternalLoader.VRMLLoader: Lexing errors detected." );

    }

    return lexingResult;

  }

}

class VRMLParser extends CstParser {

  constructor( tokenVocabulary ) {

    super( tokenVocabulary );

    const $ = this;

    const Version = tokenVocabulary[ "Version" ];
    const LCurly = tokenVocabulary[ "LCurly" ];
    const RCurly = tokenVocabulary[ "RCurly" ];
    const LSquare = tokenVocabulary[ "LSquare" ];
    const RSquare = tokenVocabulary[ "RSquare" ];
    const Identifier = tokenVocabulary[ "Identifier" ];
    const RouteIdentifier = tokenVocabulary[ "RouteIdentifier" ];
    const StringLiteral = tokenVocabulary[ "StringLiteral" ];
    const HexLiteral = tokenVocabulary[ "HexLiteral" ];
    const NumberLiteral = tokenVocabulary[ "NumberLiteral" ];
    const TrueLiteral = tokenVocabulary[ "TrueLiteral" ];
    const FalseLiteral = tokenVocabulary[ "FalseLiteral" ];
    const NullLiteral = tokenVocabulary[ "NullLiteral" ];
    const DEF = tokenVocabulary[ "DEF" ];
    const USE = tokenVocabulary[ "USE" ];
    const ROUTE = tokenVocabulary[ "ROUTE" ];
    const TO = tokenVocabulary[ "TO" ];
    const NodeName = tokenVocabulary[ "NodeName" ];

    $.RULE( "vrml", function () {

      $.SUBRULE( $.version );
      $.AT_LEAST_ONE( function () {

        $.SUBRULE( $.node );

      } );
      $.MANY( function () {

        $.SUBRULE( $.route );

      } );

    } );

    $.RULE( "version", function () {

      $.CONSUME( Version );

    } );

    $.RULE( "node", function () {

      $.OPTION( function () {

        $.SUBRULE( $.def );

      } );

      $.CONSUME( NodeName );
      $.CONSUME( LCurly );
      $.MANY( function () {

        $.SUBRULE( $.field );

      } );
      $.CONSUME( RCurly );

    } );

    $.RULE( "field", function () {

      $.CONSUME( Identifier );

      $.OR2( [
        { ALT: function () {

            $.SUBRULE( $.singleFieldValue );

          } },
        { ALT: function () {

            $.SUBRULE( $.multiFieldValue );

          } }
      ] );

    } );

    $.RULE( "def", function () {

      $.CONSUME( DEF );
      $.OR( [
        { ALT: function () {

            $.CONSUME( Identifier );

          } },
        { ALT: function () {

            $.CONSUME( NodeName );

          } }
      ] );

    } );

    $.RULE( "use", function () {

      $.CONSUME( USE );
      $.OR( [
        { ALT: function () {

            $.CONSUME( Identifier );

          } },
        { ALT: function () {

            $.CONSUME( NodeName );

          } }
      ] );

    } );

    $.RULE( "singleFieldValue", function () {

      $.AT_LEAST_ONE( function () {

        $.OR( [
          { ALT: function () {

              $.SUBRULE( $.node );

            } },
          { ALT: function () {

              $.SUBRULE( $.use );

            } },
          { ALT: function () {

              $.CONSUME( StringLiteral );

            } },
          { ALT: function () {

              $.CONSUME( HexLiteral );

            } },
          { ALT: function () {

              $.CONSUME( NumberLiteral );

            } },
          { ALT: function () {

              $.CONSUME( TrueLiteral );

            } },
          { ALT: function () {

              $.CONSUME( FalseLiteral );

            } },
          { ALT: function () {

              $.CONSUME( NullLiteral );

            } }
        ] );


      } );

    } );

    $.RULE( "multiFieldValue", function () {

      $.CONSUME( LSquare );
      $.MANY( function () {

        $.OR( [
          { ALT: function () {

              $.SUBRULE( $.node );

            } },
          { ALT: function () {

              $.SUBRULE( $.use );

            } },
          { ALT: function () {

              $.CONSUME( StringLiteral );

            } },
          { ALT: function () {

              $.CONSUME( HexLiteral );

            } },
          { ALT: function () {

              $.CONSUME( NumberLiteral );

            } },
          { ALT: function () {

              $.CONSUME( NullLiteral );

            } }
        ] );

      } );
      $.CONSUME( RSquare );

    } );

    $.RULE( "route", function () {

      $.CONSUME( ROUTE );
      $.CONSUME( RouteIdentifier );
      $.CONSUME( TO );
      $.CONSUME2( RouteIdentifier );

    } );

    this.performSelfAnalysis();

  }

}

class Object3d {}
class Group extends Object3d {
  constructor() {
    super();
    this.elements = [];
  }
}
class Mesh extends Object3d {
  constructor() {
    super();
    this.geometry = null;
    this.texture = null;
  }
}
class Geometry {
  constructor() {
    this.positions = [];
    this.normals = [];
    this.uvs = [];
    this.indices = [];
    this.colors = [];
  }
}
class Texture {
  constructor() {
    this.url = "";
  }
}
